/**
 * TypedConfirmDialog — canonical destructive-action gate.
 *
 * Per SYSADMIN-PLATFORM-SPEC §11.7 ("Danger confirmation"):
 *   - Standardize on AlertDialog (Radix) for destructive ops.
 *   - Type-to-confirm field for delete only.
 *   - Suspend uses regular Dialog with required reason textarea.
 *   - Destructive buttons use `<Button variant="destructive">`, never
 *     "outline button with red text".
 *
 * Used for: factory reset tenant, delete all API keys, rotate master
 * key, disable MFA enforcement, kill MCP container, unload model, etc.
 *
 * v1 source: ported from `components/admin/typed-confirm-dialog.tsx`.
 * PR-0 elevates it to the canonical sysadmin primitive used across
 * Phase 5 PR-A..F. The legacy admin path remains as a re-export shim
 * so existing imports keep working.
 *
 * Follows research guidance: destructive actions need typed-confirm
 * plus optional 2-person approval (2-person handled by wrapping the
 * dialog in `require2Person={true}` which gates the enable state).
 *
 * @example
 * <TypedConfirmDialog
 *   open={open}
 *   onOpenChange={setOpen}
 *   title="Delete tenant"
 *   description="This will permanently delete the tenant and all its data."
 *   consequences={[
 *     'All users in the tenant lose access',
 *     'All audit logs are archived but read-only',
 *     'Cannot be undone',
 *   ]}
 *   confirmPhrase="acme-corp"
 *   confirmLabel="Delete tenant"
 *   requireReason
 *   onConfirm={({ reason }) => mutate({ reason })}
 * />
 */

import { useState, useEffect } from 'react';
import { AlertTriangle, Lock, UserCheck } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter,
} from './dialog';
import { Button } from './button';
import { Input } from './input';
import { Label } from './label';
import { Textarea } from './textarea';
import { Checkbox } from './checkbox';
import { cn } from '@/lib/utils';

export interface TypedConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Title of the action being confirmed */
  title: string;
  /** Description of consequences */
  description: string;
  /** Bullets explaining what will happen */
  consequences?: string[];
  /** The exact phrase the user must type to confirm */
  confirmPhrase: string;
  /** Label on the confirm button */
  confirmLabel: string;
  /** Require a reason textarea (typically for audit trail) */
  requireReason?: boolean;
  reasonPlaceholder?: string;
  /**
   * Minimum reason length the confirm button waits for. Defaults to 5,
   * the historic value every existing caller relies on.
   *
   * P2 T1 added the prop because the air-gap posture setter's Zod body
   * requires `reason.min(10)`: with a fixed 5-char gate the dialog would
   * happily enable Confirm on a 7-character reason and the operator
   * would meet a server-side 400 they could not have predicted. A
   * caller whose backend is stricter passes its own bound so the client
   * gate and the server contract agree.
   */
  reasonMinLength?: number;
  /**
   * Keep the dialog mounted after Confirm instead of closing it (P2
   * review R2 / P2-BF-06).
   *
   * The default (`true`) calls `onOpenChange(false)` the instant
   * `onConfirm` returns, which is right for a fire-and-forget confirm but
   * wrong for a mutation whose failure the operator must see: the dialog
   * — and, where the caller renders it conditionally, the component that
   * owns the mutation — unmounts before the server replies, so a 401
   * STEP_UP_REQUIRED / 403 / 422 lands nowhere.
   *
   * Pass `false` and the caller owns closing: close in the mutation's
   * `onSuccess`, and pass the failure back through `errorMessage`.
   */
  closeOnConfirm?: boolean;
  /**
   * Error to render inside the dialog (used with `closeOnConfirm={false}`).
   * Typed text and the reason are preserved, so the operator can retry
   * without re-typing.
   */
  errorMessage?: string | null;
  /** Disable Confirm while the caller's mutation is in flight. */
  pending?: boolean;
  /** Require a second admin's name (mock for 2-person approval) */
  require2Person?: boolean;
  /** Accent variant */
  variant?: 'danger' | 'warning';
  onConfirm: (payload: { reason?: string; secondApprover?: string }) => void;
}

export function TypedConfirmDialog({
  open, onOpenChange,
  title, description, consequences = [],
  confirmPhrase, confirmLabel,
  requireReason = false, reasonPlaceholder, reasonMinLength = 5,
  closeOnConfirm = true, errorMessage = null, pending = false,
  require2Person = false,
  variant = 'danger',
  onConfirm,
}: TypedConfirmDialogProps) {
  const [typed, setTyped] = useState('');
  const [reason, setReason] = useState('');
  const [secondApprover, setSecondApprover] = useState('');
  const [acknowledged, setAcknowledged] = useState(false);

  useEffect(() => {
    if (open) {
      setTyped('');
      setReason('');
      setSecondApprover('');
      setAcknowledged(false);
    }
  }, [open]);

  const phraseMatches = typed === confirmPhrase;
  const reasonOk = !requireReason || reason.trim().length >= reasonMinLength;
  const approverOk = !require2Person || secondApprover.trim().length >= 2;

  const canConfirm = phraseMatches && acknowledged && reasonOk && approverOk;

  const handleConfirm = () => {
    if (!canConfirm) return;
    onConfirm({
      reason: requireReason ? reason.trim() : undefined,
      secondApprover: require2Person ? secondApprover.trim() : undefined,
    });
    // R2 / P2-BF-06: only auto-close when the caller has not taken over
    // the lifecycle. When it has, it closes on success and feeds failures
    // back through `errorMessage`.
    if (closeOnConfirm) onOpenChange(false);
  };

  const accent = variant === 'danger' ? {
    icon: 'text-red-600 dark:text-red-400',
    border: 'border-red-500/40',
    bg: 'bg-red-500/5',
    headingText: 'text-red-900 dark:text-red-200',
    bodyText: 'text-red-800 dark:text-red-300',
    button: 'bg-red-600 hover:bg-red-700',
  } : {
    icon: 'text-amber-600 dark:text-amber-400',
    border: 'border-amber-500/40',
    bg: 'bg-amber-500/5',
    headingText: 'text-amber-900 dark:text-amber-200',
    bodyText: 'text-amber-800 dark:text-amber-300',
    button: 'bg-amber-600 hover:bg-amber-700',
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className={cn('h-5 w-5', accent.icon)} />
            {title}
          </DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        {consequences.length > 0 && (
          <div className={cn('rounded-md border p-3 space-y-2', accent.border, accent.bg)}>
            <p className={cn('text-xs font-semibold', accent.headingText)}>
              This will:
            </p>
            <ul className={cn('list-disc pl-4 space-y-0.5 text-xs', accent.bodyText)}>
              {consequences.map((c, i) => <li key={i}>{c}</li>)}
            </ul>
          </div>
        )}

        <label className="flex items-start gap-2 cursor-pointer">
          <Checkbox
            checked={acknowledged}
            onCheckedChange={(c) => setAcknowledged(c === true)}
            className="mt-0.5"
          />
          <span className="text-xs">
            I understand the consequences and want to proceed.
          </span>
        </label>

        {requireReason && (
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold">
              Reason <span className="text-red-500">*</span>
            </Label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={reasonPlaceholder ?? 'Explain why this action is necessary…'}
              rows={2}
              className="text-xs"
            />
            <p className="text-[10px] text-muted-foreground">
              Required for audit trail. Minimum {reasonMinLength} characters.
            </p>
          </div>
        )}

        {require2Person && (
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold flex items-center gap-1">
              <UserCheck className="h-3 w-3" />
              Second approver name <span className="text-red-500">*</span>
            </Label>
            <Input
              value={secondApprover}
              onChange={(e) => setSecondApprover(e.target.value)}
              placeholder="e.g., Mike Rodriguez"
              className="h-9 text-xs"
            />
            <p className="text-[10px] text-muted-foreground">
              Another admin must approve. They'll be notified via your configured notification channels.
            </p>
          </div>
        )}

        <div className="space-y-1.5">
          <Label className="text-xs font-semibold flex items-center gap-1">
            <Lock className="h-3 w-3" />
            Type <code className="bg-muted px-1.5 py-0.5 rounded text-[11px]">{confirmPhrase}</code> to confirm
          </Label>
          <Input
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            placeholder={confirmPhrase}
            className={cn(
              'h-9 text-xs font-mono',
              typed.length > 0 && !phraseMatches && 'border-red-500',
              phraseMatches && 'border-emerald-500',
            )}
            autoComplete="off"
            spellCheck={false}
          />
        </div>

        {errorMessage && (
          <div
            role="alert"
            data-testid="typed-confirm-error"
            className="rounded border border-destructive/40 bg-destructive/5 p-2 text-xs text-destructive"
          >
            {errorMessage}
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            onClick={handleConfirm}
            disabled={!canConfirm || pending}
            className={accent.button}
          >
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Internal helper exposed for tests — pure logic for whether the
// confirm button should be enabled.
export function _isTypedConfirmEnabled(args: {
  typed: string;
  confirmPhrase: string;
  acknowledged: boolean;
  requireReason: boolean;
  reason: string;
  require2Person: boolean;
  secondApprover: string;
  /** Mirrors the `reasonMinLength` prop; defaults to the historic 5. */
  reasonMinLength?: number;
}): boolean {
  const phraseMatches = args.typed === args.confirmPhrase;
  const reasonOk =
    !args.requireReason ||
    args.reason.trim().length >= (args.reasonMinLength ?? 5);
  const approverOk = !args.require2Person || args.secondApprover.trim().length >= 2;
  return phraseMatches && args.acknowledged && reasonOk && approverOk;
}
